import { useMemo, useState } from 'react';
import { History } from 'lucide-react';
import {
  LiveScreen, QueryState, SearchBox, StatusPill, formatDateTime, useDebounced,
} from '@/components/live';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import type { AuditRow } from '@/types/records';

/**
 * The audit timeline.
 *
 * `audit.change_events` is append-only - no policy permits an update or a delete, and a
 * trigger raises on either - so this screen is read-only by construction rather than by
 * politeness. Values are redacted before they are written, so a mobile number never reaches
 * the log in the first place and there is nothing here to mask on the way out.
 *
 * Each line is the sentence the database generated when the change happened, not one composed
 * now: a summary written at read time would describe today's schema, not what actually
 * occurred.
 */
export default function AuditTimelineScreen() {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const [stat, setStat] = useState<string | null>(null);

  const events = useRows<AuditRow>('audit_timeline', {
    search: debounced
      ? { columns: ['entity_label', 'actor_label', 'natural_language_summary', 'entity_table'], term: debounced }
      : undefined,
    orderBy: { column: 'event_at', ascending: false },
    limit: 300,
  });

  // Event types are the ones the row trigger and the access/export loggers write. A soft
  // delete and a hard delete are one card: to the reader both mean the record went away.
  const STATS = useMemo(() => [
    { key: 'all', label: 'Events', match: () => true, tone: 'neutral' as const },
    { key: 'create', label: 'Created', tone: 'ok' as const, match: (row: AuditRow) => row.event_type === 'create' },
    { key: 'update', label: 'Updated', tone: 'info' as const, match: (row: AuditRow) => row.event_type === 'update' },
    { key: 'delete', label: 'Deleted', tone: 'error' as const,
      match: (row: AuditRow) => row.event_type === 'delete' || row.event_type === 'soft_delete' },
    { key: 'restore', label: 'Restored', tone: 'info' as const, match: (row: AuditRow) => row.event_type === 'restore' },
    { key: 'access', label: 'Viewed or exported', tone: 'info' as const,
      match: (row: AuditRow) => row.event_type === 'access' || row.event_type === 'export' },
    { key: 'risk', label: 'High risk', tone: 'warn' as const,
      match: (row: AuditRow) => row.risk_level === 'high' || row.risk_level === 'critical' },
  ], []);

  const visible = useMemo(() => applyStat(events.data ?? [], STATS, stat), [events.data, STATS, stat]);

  return (
    <LiveScreen
      subtitle="Every change, as it was recorded. Append-only, and sensitive values were redacted before they were written."
      title="Audit timeline"
    >
      <div className="toolbar-row">
        <SearchBox onChange={setSearch} placeholder="Record, person or action" value={search} />
      </div>

      <StatCards active={stat} onSelect={setStat} rows={events.data ?? []} stats={STATS} />

      <QueryState empty="Nothing has been recorded yet." query={events}>
        <ol className="audit-timeline">
          {visible.map((event) => (
            <li key={event.id}>
              <div className="audit-mark"><History size={13} /></div>
              <div className="audit-body">
                <p>{event.natural_language_summary ?? `${event.event_verb ?? event.event_type} ${event.entity_label ?? ''}`}</p>
                <div className="audit-meta">
                  <span>{formatDateTime(event.event_at)}</span>
                  {event.actor_label && <span>{event.actor_label}</span>}
                  <span className="muted">{event.entity_table}</span>
                  {event.risk_level && event.risk_level !== 'low' && <StatusPill value={event.risk_level} />}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </QueryState>
    </LiveScreen>
  );
}
